import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { SettingService } from './models/setting.service';
import { resolveShopIdForRequest } from '../storage/shop-id.storage';

export const INSTALLATION_SETTING_NAME = 'installation';
export const WELCOME_WIZARD_PATH = '/bienvenue';

@Injectable({ providedIn: 'root' })
export class WelcomeWizardService {
  constructor(
    private settingService: SettingService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Pour le guard : vérifie installation avant tout rendu de composant.
   * Utilise le cache settings si déjà chargé (pas de double requête lors des navigations suivantes).
   */
  checkForGuard(targetUrl: string): Observable<boolean | UrlTree> {
    if (targetUrl.startsWith(WELCOME_WIZARD_PATH)) {
      return of(true);
    }
    // Aucun shop précisé (-1 = « Tous les magasins ») → pas de wizard.
    if (resolveShopIdForRequest(isPlatformBrowser(this.platformId)) === -1) {
      return of(true);
    }
    if (this.settingService.settings.length > 0) {
      const installation = this.settingService.getSettingByName(INSTALLATION_SETTING_NAME);
      const isFirstRun = SettingService.isTruthySettingValue(installation?.value);
      return of(isFirstRun ? this.router.createUrlTree([WELCOME_WIZARD_PATH]) : true);
    }
    return this.settingService.all({ skipLoader: true }).pipe(
      map((res: any) => {
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        this.settingService.setSettings(list);
        const installation = list.find((s: any) => s.name === INSTALLATION_SETTING_NAME);
        const isFirstRun = SettingService.isTruthySettingValue(installation?.value);
        return isFirstRun ? this.router.createUrlTree([WELCOME_WIZARD_PATH]) : true;
      }),
      catchError(() => of(true as boolean | UrlTree))
    );
  }

  /**
   * Vérifie si le paramètre "installation" est à 1.
   * Si oui, redirige vers la page du wizard de bienvenue.
   */
  checkInstallation(): void {
    this.settingService.all({ skipLoader: true }).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        this.settingService.setSettings(list);
        const installation = list.find((s: any) => s.name === INSTALLATION_SETTING_NAME);
        const isFirstRun = installation?.value === '1' || installation?.value === 1;
        const isWelcomePage = this.router.url.includes('/bienvenue');
        if (isFirstRun && !isWelcomePage) {
          this.router.navigate([WELCOME_WIZARD_PATH]);
        }
      },
      error: () => {}
    });
  }

  /**
   * Termine le wizard après store-init-settings : met à jour le cache et redirige (sans appel API).
   */
  finishFromStoreInit(): void {
    const installation = this.settingService.getSettingByName(INSTALLATION_SETTING_NAME);
    if (installation) {
      this.settingService.setSettingValue(INSTALLATION_SETTING_NAME, '0');
    }
  }

  /**
   * Termine le wizard : met à jour le paramètre installation à 0 et redirige vers le tableau de bord.
   */
  setInstallationDone(): void {
    const installation = this.settingService.getSettingByName(INSTALLATION_SETTING_NAME);
    if (!installation) {
      this.router.navigate(['/moni', 'dashboard']);
      return;
    }
    this.settingService.edit(installation.id, { value: '0' }).subscribe({
      next: () => {
        this.settingService.setSettingValue(INSTALLATION_SETTING_NAME, '0');
        this.router.navigate(['/moni', 'dashboard']);
      },
      error: () => {
        this.router.navigate(['/moni', 'dashboard']);
      }
    });
  }
}
